import { useEffect, useRef, useCallback, useMemo } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import leaflet from 'leaflet'
import { renderToString } from 'react-dom/server'
import PropTypes from 'prop-types'
import { usePricingTypes } from 'components/common/hooks/usePricingTypes'
import { useFormatPolePrice } from 'components/common/hooks/useFormatPolePrice'
import { useTranslation } from 'react-i18next'
import { useShowToast } from 'components/common/hooks/useShowToast'
import { upperFirstOnly } from 'utils/format'
import { NEARBY_STATIONS_QUERY } from '../../../../CompetitorPricingQueries'
import { ADD_STATION_COMPETITOR_MUTATION } from '../../../../CompetitorPricingMutations'
import { BrandChip } from '../../brand-monograms'
import classNames from 'classnames/bind'
import styles from './NearbyStationsLayer.module.scss'

const cx = classNames.bind(styles)
const DEBOUNCE_MS = 300

// A cash/credit station returns a CASH and a CREDIT row per fuel grade; a
// single-price station returns one ALL row. Pick the prices that match the
// currently selected pricing type (mirroring `useVisiblePrices` on the tracked
// markers) and label cash/credit explicitly, so we never silently show the
// first of two rows unlabelled.
const buildVisiblePrices = (prices = [], fuelGrades, selectedPricingType) => {
  const stationHasCashCredit = prices.some(
    (price) => price.pricingType === 'CASH' || price.pricingType === 'CREDIT'
  )

  return fuelGrades.flatMap(({ id, shortTitle }) =>
    prices
      .filter((price) => {
        if (price.fuelGradeId !== id) return false
        const priceType = price.pricingType
        if (
          priceType !== 'ALL' &&
          selectedPricingType !== 'ALL' &&
          selectedPricingType !== priceType
        ) return false
        if (stationHasCashCredit && priceType === 'ALL') return false
        return true
      })
      .map((price) => ({
        key: `${price.pricingType}-${id}`,
        label:
          price.pricingType === 'ALL'
            ? shortTitle
            : `${shortTitle} (${upperFirstOnly(price.pricingType)})`,
        price: price.price,
      }))
  )
}

const createGenericIcon = (station) => {
  return new leaflet.divIcon({
    html: renderToString(
      <div className={cx('nearby-marker')}>
        <BrandChip brand={station.brand} size={31} />
      </div>
    ),
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
    className: '',
  })
}

const NearbyStationPopup = ({ station, fuelGrades, stationId }) => {
  const { t } = useTranslation(['translations'])
  const { formatPolePrice } = useFormatPolePrice()
  const { selectedPricingType } = usePricingTypes()
  const { showErrorToast } = useShowToast()
  const map = useMap()

  const visiblePrices = useMemo(
    () => buildVisiblePrices(station.polePrices, fuelGrades, selectedPricingType),
    [station.polePrices, fuelGrades, selectedPricingType]
  )

  const [addCompetitor, { loading }] = useMutation(
    ADD_STATION_COMPETITOR_MUTATION,
    {
      variables: { stationId, competitorId: station.id },
      refetchQueries: ['competitorPricingQuery'],
      onCompleted: () => map.closePopup(),
      onError: () => showErrorToast(),
    }
  )

  return (
    <Popup className={cx('popup-wrapper')}>
      <div className={cx('popup-head')}>
        <BrandChip brand={station.brand} size={30} />
        <div className={cx('popup-title')}>
          <div className={cx('popup-name')}>{station.name}</div>
          <div className={cx('popup-sub')}>
            {station.brand?.name} &middot; {t('widgets.competitorPricing.availableNearby')}
          </div>
        </div>
      </div>
      {visiblePrices.length > 0 ? (
        <div className={cx('popup-list')}>
          {visiblePrices.map(({ key, label, price }) => (
            <div key={key} className={cx('popup-row')}>
              <span>{label}</span>
              <span className={cx('popup-price')}>{formatPolePrice(price)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={cx('popup-no-data')}>{t('widgets.competitorPricing.missingPriceData')}</div>
      )}
      <div className={cx('popup-foot')}>
        <button
          className={cx('popup-add-btn')}
          onClick={() => addCompetitor()}
          disabled={loading}
        >
          {loading
            ? '...'
            : t('widgets.competitorPricing.addToCompetitorList')}
        </button>
      </div>
    </Popup>
  )
}

NearbyStationPopup.propTypes = {
  station: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    brand: PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    }),
    polePrices: PropTypes.array,
  }).isRequired,
  fuelGrades: PropTypes.array.isRequired,
  stationId: PropTypes.string.isRequired,
}

const NearbyStationsLayer = ({ stationId, competitorIds, fuelGrades }) => {
  const { supportedPricingTypes } = usePricingTypes()
  const timeoutRef = useRef(null)
  const map = useMap()

  const [fetchNearbyStations, { data }] = useLazyQuery(
    NEARBY_STATIONS_QUERY,
    { fetchPolicy: 'network-only' }
  )
  const previousDataRef = useRef(data)

  useEffect(() => {
    if (data?.nearbyStations) previousDataRef.current = data
  }, [data])

  const displayData = data ?? previousDataRef.current

  const fetchForBounds = useCallback(
    (mapInstance) => {
      if (!supportedPricingTypes) return
      const bounds = mapInstance.getBounds()
      fetchNearbyStations({
        variables: {
          northeastLat: bounds.getNorthEast().lat,
          northeastLng: bounds.getNorthEast().lng,
          southwestLat: bounds.getSouthWest().lat,
          southwestLng: bounds.getSouthWest().lng,
          pricingTypes: supportedPricingTypes,
        },
      })
    },
    [fetchNearbyStations, supportedPricingTypes]
  )

  useMapEvents({
    movestart: () => {
      clearTimeout(timeoutRef.current)
    },
    moveend: () => {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => fetchForBounds(map), DEBOUNCE_MS)
    },
  })

  useEffect(() => {
    fetchForBounds(map)
    return () => clearTimeout(timeoutRef.current)
  }, [fetchForBounds, map])

  const nonCompetitorStations = useMemo(() => {
    const stations = displayData?.nearbyStations ?? []
    return stations.filter((station) => station.id !== stationId && !competitorIds.has(station.id))
  }, [displayData, competitorIds, stationId])

  return nonCompetitorStations.map((station) => (
    <Marker
      key={station.id}
      position={[station.latitude, station.longitude]}
      icon={createGenericIcon(station)}
    >
      <NearbyStationPopup station={station} fuelGrades={fuelGrades} stationId={stationId} />
    </Marker>
  ))
}

NearbyStationsLayer.propTypes = {
  stationId: PropTypes.string.isRequired,
  competitorIds: PropTypes.instanceOf(Set).isRequired,
  fuelGrades: PropTypes.array.isRequired,
}

export { NearbyStationsLayer, buildVisiblePrices }
